import { FC, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/slice';
import { ToastMessagesSlice } from '../../store/slice/ToastMessages';

// PACKAGES
import { Spinner } from 'react-bootstrap';

// API
import apibridge from '../../apibridge';

// COMPONENTS
import TitleSection, { TitleSectionType } from '../../components/TitleSection/TitleSection';
import TeachersList from '../../components/TeachersList/TeachersList';
import SvgMask from '../../components/_Helpers/SvgMask';
import SortDropdown, { SortOptionsType } from '../../components/TitleSection/SortDropdown';

// UTILS
import { guid } from '../../libs/utils';
import DelayedFadeIn from '../../components/_Helpers/DelayedFadeIn';

// TYPES
import { ClassListClassCollection, StaffDashboardActiveMember, StaffDashboardPendingRequest } from '../../api/models';
import { UserType } from '../../store/slice/User';
import { DynamicFtux, useDynamicFtuxRestart } from '../../components/Ftux';

import { getTeacherManagementConfig } from '../../components/Ftux/configs';
import { useFtuxHighlight } from '../../components/Ftux/useFtuxHighlight';

export type TabStatus = 'Active' | 'Pending';

interface PasswordActionResult {
	isSuccess: boolean;
	validationError?: string;
}

const Teachers: FC = () => {
	const [tabStatus, setTabStatus] = useState<TabStatus>('Active');
	const [classList, setClassList] = useState<ClassListClassCollection>();
	const [activeTeachers, setActiveTeachers] = useState<Array<StaffDashboardActiveMember>>([]);
	const [pendingTeachers, setPendingTeachers] = useState<Array<StaffDashboardPendingRequest>>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [invitesLeft, setInvitesLeft] = useState(0);

	const dispatch = useAppDispatch();
	const user: UserType = useAppSelector((state) => state.user);
	const isAdmin = user.profiles?.[0].type === 'OrganisationAdministrator';
	const hasAddedStaff = user.profiles?.[0].staffAdded;

	const teacherAdminConfig = useMemo(() => getTeacherManagementConfig(), []);
	const { restartTutorial, manualRestartTrigger } = useDynamicFtuxRestart('TeacherManagement');

	const shouldHighlightInviteTeacherBtn = useFtuxHighlight('invite-teachers-button', 'teacher-management-ftux');
	const shouldHighlightActiveView = useFtuxHighlight('active-teachers', 'teacher-management-ftux');
	const shouldHighlightPendingTab = useFtuxHighlight('pending-button', 'teacher-management-ftux');
	const shouldHighLightActiveMenu = useFtuxHighlight('teacher-menu', 'teacher-management-ftux');

	const sortOptions: SortOptionsType[] = [
		{
			value: 'asc',
			label: 'Name A-Z'
		},
		{
			value: 'desc',
			label: 'Name Z-A'
		}
	];

	const [selectedSortOption, setSelectedSortOption] = useState<SortOptionsType>(sortOptions[0]);

	const navigate = useNavigate();

	const getDashboardDetails = async () => {
		const response = await apibridge.getStaffDashboard();
		if (response && response.data && !response.data.isError && response.data.result) {
			const { active = [], pending = [] } = response.data.result;
			setActiveTeachers(active);
			setPendingTeachers(pending);
		}
	};

	const getClassList = async () => {
		const response = await apibridge.getClassList();
		if (response && response.data && !response.data.isError && response.data.result) {
			setClassList(response.data.result);
		}
	};

	const getInvitesLeft = async () => {
		const response = await apibridge.getInvitesLeft();
		if (response && response.data && !response.data.isError && response.data.result) {
			const { invitesLeft = 0, isUnilimited } = response.data.result;
			setInvitesLeft(isUnilimited ? Infinity : invitesLeft);
		}
	};

	const getInitialData = async () => {
		await Promise.all([getDashboardDetails(), getClassList(), getInvitesLeft()]);
		setIsLoading(false);
	};

	useEffect(() => {
		getInitialData();
	}, []); // eslint-disable-line

	// Pending Teachers functions
	const revokeInvitation = async (id: string) => {
		setIsLoading(true);

		const response = await apibridge.deleteStaffInviteRevoke({ profileId: id });
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				await Promise.all([getDashboardDetails(), getInvitesLeft()]);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Revoke invitation error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	const resendInvitation = async (id: string) => {
		setIsLoading(true);

		const response = await apibridge.postStaffInviteResend({ profileId: id });
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Teacher invitation resent',
						description: 'An invite link has been resent to this email address.'
					})
				);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Resend invitation error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	// Active Teachers functions
	const revokeAccess = async (id: string, password: string): Promise<PasswordActionResult> => {
		// Find the teacher before revoking access to get their name
		const teacherDetails = activeTeachers.find((teacher) => teacher.id === id);
		const teacherName = teacherDetails
			? `${teacherDetails.firstName || ''} ${teacherDetails.lastName || ''}`.trim() ||
				teacherDetails.email ||
				'a teacher'
			: 'a teacher';

		const response = await apibridge.deleteStaffDelete({ profileId: id, password });
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Teacher removed and access revoked',
						description: `You have revoked access for ${teacherName} and they have been removed from your organisation.`
					})
				);

				await getDashboardDetails();

				return { isSuccess: true };
			} else if (response.data.validationErrors) {
				return { isSuccess: false, validationError: response.data.validationErrors[0]?.reason || 'Unknown error' };
			} else {
				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'danger',
						heading: 'Revoke access error',
						description: 'Something went wrong. Please try again.'
					})
				);
			}
		} else {
			dispatch(
				ToastMessagesSlice.actions.add({
					id: guid(),
					type: 'danger',
					heading: 'Revoke access error',
					description: 'Something went wrong. Please try again.'
				})
			);
		}
		return { isSuccess: false };
	};

	const removeAdminRole = async (id: string, password: string): Promise<PasswordActionResult> => {
		const response = await apibridge.postAdminRevoke({ profileId: id, password });
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				await getDashboardDetails();

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Admin rights removed',
						description: 'Admin rights were removed successfully.'
					})
				);

				return { isSuccess: true };
			} else if (response.data.validationErrors) {
				return { isSuccess: false, validationError: response.data.validationErrors[0]?.reason || 'Unknown error' };
			} else {
				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'danger',
						heading: 'Remove admin rights error',
						description: 'Something went wrong. Please try again.'
					})
				);
			}
		} else {
			dispatch(
				ToastMessagesSlice.actions.add({
					id: guid(),
					type: 'danger',
					heading: 'Remove admin rights error',
					description: 'Something went wrong. Please try again.'
				})
			);
		}
		return { isSuccess: false };
	};

	const makeAdmin = async (id: string, password: string): Promise<PasswordActionResult> => {
		const response = await apibridge.postAdminGrant({ profileId: id, password });
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				await getDashboardDetails();
				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Admin rights granted',
						description: 'Admin rights were updated successfully.'
					})
				);
				return { isSuccess: true };
			} else if (response.data.validationErrors) {
				return { isSuccess: false, validationError: response.data.validationErrors[0]?.reason || 'Unknown error' };
			} else {
				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'danger',
						heading: 'Give admin rights error',
						description: 'Something went wrong. Please try again.'
					})
				);
			}
		} else {
			dispatch(
				ToastMessagesSlice.actions.add({
					id: guid(),
					type: 'danger',
					heading: 'Give admin rights error',
					description: 'Something went wrong. Please try again.'
				})
			);
		}
		return { isSuccess: false };
	};

	const assignClass = async (id: string, classId: string) => {
		setIsLoading(true);

		const response = await apibridge.postStaffClassAssign({ profileId: id, classId });
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				await Promise.all([getDashboardDetails(), getClassList()]);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assign class error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	const unassignClass = async (id: string, classId: string) => {
		setIsLoading(true);

		const response = await apibridge.deleteStaffClassRemove({ profileId: id, classId });
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				await Promise.all([getDashboardDetails(), getClassList()]);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Profile error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	const handleInviteTeacherClick = () => {
		if (invitesLeft > 0) {
			navigate('invite');
		} else {
			dispatch(
				ToastMessagesSlice.actions.add({
					id: guid(),
					type: 'danger',
					heading: 'Your organisation is full',
					description:
						'You have reached the maximum number of seats within your organisation and are unable to invite more staff.'
				})
			);
		}
	};

	const titleSectionProps: TitleSectionType = {
		title: 'Teacher Management',
		description: 'Manage your teachers and staff across the organisation.'
	};
	if (user.permissions?.includes('StaffInvite')) {
		titleSectionProps.primaryButtonText = 'Invite Teachers';
		titleSectionProps.primaryButtonIcon = '/svg/invite.svg';
		titleSectionProps.primaryButtonClick = handleInviteTeacherClick;
		titleSectionProps.primaryButtonHighlight = shouldHighlightInviteTeacherBtn;
		titleSectionProps.primaryButtonClickId = 'invite-teachers-button';

		if (isAdmin && !hasAddedStaff) titleSectionProps.primaryButtonClass = 'show-dot';
	}

	return (
		<div className="teachers-page">
			<TitleSection {...titleSectionProps} isHelpSection={true} onHelpClick={() => restartTutorial()}>
				<div className="d-flex justify-content-between">
					<div className="position-relative w-auto align-items-center">
						<nav className="nav nav-pills flex-row">
							<button
								onClick={() => setTabStatus('Active')}
								className={`nav-pill h4 ${tabStatus === 'Active' ? 'active' : ''}`}
							>
								Active
							</button>
							<button
								id="pending-button"
								onClick={() => setTabStatus('Pending')}
								className={`nav-pill h4 ${tabStatus === 'Pending' ? 'active' : ''} ${
									shouldHighlightPendingTab ? 'highlight-box' : ''
								}`}
							>
								Pending
							</button>
						</nav>
					</div>
					<div className="sort-container d-flex align-items-center">
						<div className="d-flex align-items-center gap-1 h4 text-nowrap">
							<SvgMask path="/svg/sort.svg" width={24} height={24} />
							<strong>Sort by</strong>
						</div>
						<SortDropdown
							sortOptions={sortOptions}
							selectedSortOption={selectedSortOption}
							setSelectedSortOption={setSelectedSortOption}
						/>
					</div>
				</div>
			</TitleSection>
			<div className="container">
				{isLoading ? (
					<div className="d-flex align-items-center justify-content-center position-relative w-100">
						<DelayedFadeIn>
							<Spinner animation="border" role="status">
								<span className="visually-hidden">Loading...</span>
							</Spinner>
						</DelayedFadeIn>
					</div>
				) : tabStatus === 'Active' ? (
					<TeachersList
						revokeAccess={revokeAccess}
						removeAdminRole={removeAdminRole}
						makeAdmin={makeAdmin}
						assignClass={assignClass}
						unassignClass={unassignClass}
						teachersList={activeTeachers}
						classList={classList}
						tab={'Active'}
						sort={selectedSortOption}
						highlightContainer={shouldHighlightActiveView}
						highlightMenu={shouldHighLightActiveMenu}
					/>
				) : tabStatus === 'Pending' ? (
					<TeachersList
						revokeInvitation={revokeInvitation}
						resendInvitation={resendInvitation}
						teachersList={pendingTeachers}
						tab={'Pending'}
						sort={selectedSortOption}
					/>
				) : null}
			</div>
			<DynamicFtux
				config={teacherAdminConfig}
				tutorialType="TeacherManagement"
				manualRestartTrigger={manualRestartTrigger}
			/>
		</div>
	);
};

export default Teachers;
